import { motion } from "framer-motion";
import { Link } from "@tanstack/react-router";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { blogPosts } from "@/data";
import { formatDate } from "@/lib/utils";

export function BlogPreviewSection() {
  const featured = blogPosts.filter((p) => p.featured).slice(0, 3);

  return (
    <section className="py-24 bg-cream">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold">Latest Updates</h2>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 font-semibold text-burgundy hover:text-[#4A1219] transition-colors"
            >
              View All Articles
              <ArrowRight size={15} />
            </Link>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {featured.map((post, i) => (
            <motion.article
              key={post.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="group"
            >
              <Link to={`/blog/${post.slug}` as never}>
                <div className="aspect-[16/9] bg-burgundy/8 rounded-xl mb-5 overflow-hidden relative">
                  {post.coverImage ? (
                    <img
                      src={post.coverImage}
                      alt={post.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-burgundy/15 flex items-center justify-center">
                        <span className="text-2xl font-bold text-burgundy/40">
                          {post.category.charAt(0)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <div className="flex items-center gap-4 text-sm text-neutral-800 mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar size={11} />
                      {formatDate(post.publishedAt)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={11} />
                      {post.readTime} min read
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold leading-snug mb-3 group-hover:text-burgundy transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-sm text-neutral-800 leading-relaxed line-clamp-2 mb-4">
                    {post.excerpt}
                  </p>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
